"use client"
import Link from "next/link";
import { useMemo } from "react";
import { useEditorsPicks } from "@/lib/hooks/useSiteData";
import type { BlogPost } from "@/types";

const FALLBACK_IMAGE = "/images/boxes/card_1.jpg";
const FALLBACK_EXCERPT = "Read more about this topic...";

const getSpecificationValue = (post: BlogPost, key: string) =>
  post.specifications?.find((spec) => spec.key === key)?.value;

const ArticlesSection = ({ showTitle = true }: { showTitle?: boolean }) => {
  const { data: blogResponse, loading, error } = useEditorsPicks();

  const editorPicks = useMemo(() => {
    if (!blogResponse?.data?.length) return [];

    return blogResponse.data.filter((post) => post.isActive && !post.isDeleted).map((post) => ({
      id: post.id,
      title: post.title,
      excerpt: getSpecificationValue(post, "summary") ?? FALLBACK_EXCERPT,
      image: getSpecificationValue(post, "image") ?? FALLBACK_IMAGE,
      link: `/blog/${post.slug}`,
    }));
  }, [blogResponse]);

  const [featuredArticle, ...otherArticles] = editorPicks;

  return (
    <section className="articles-sec">
      <div className="container">
        <div className="row justify-content-between">
          {showTitle && <div className="col-lg-9 col-12">
            <div className="heading">
              <h2>Recent Senior Connect Articles</h2>
            </div>
          </div>}

          {showTitle && <div className="col-lg-3 col-12 d-flex justify-content-end">
            <Link href="/blog" className="btn btn-primary">
              View All Articles
            </Link>
          </div>}

          {loading && (
            <div className="col-12">
              <p>Loading articles...</p>
            </div>
          )}

          {error && (
            <div className="col-12">
              <p className="text-danger">
                Unable to load articles right now. Please try again later.
              </p>
            </div>
          )}

          {!loading && !error && featuredArticle && (
            <div className="col-lg-12">
              <div className="main-article">
                <div className="article-tag">Featured Article</div>
                <div className="row align-items-center">
                  <div className="col-lg-7">
                    <div className="article-link">
                      <Link href={featuredArticle.link}>
                        <h4>{featuredArticle.title}</h4>
                      </Link>
                      <div className="web-tags">
                        <img src="/images/icons/web-1.png" alt="" />
                        <img src="/images/icons/web-6.svg" alt="" />
                      </div>
                    </div>
                    <p>{featuredArticle.excerpt}</p>
                    <Link href={featuredArticle.link} className="btn btn-secondary">
                      Read More
                    </Link>
                  </div>
                  <div className="col-lg-5">
                    <div className="article-image">
                      <img src={featuredArticle.image} alt={featuredArticle.title} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && otherArticles.length > 0 ? (
            otherArticles.map((article) => (
              <div className="col-lg-4" key={article.id}>
                <div className="article-card">
                  <div className="web-tags text-end">
                    <img src="/images/icons/web-1.png" alt="" />
                    <img src="/images/icons/web-6.svg" alt="" />
                    <img src="/images/icons/web-2.png" alt="" />
                    <img src="/images/icons/web-4.png" alt="" />
                  </div>
                  <div className="article-link">
                    <Link href={article.link}>
                      <h5>{article.title}</h5>
                    </Link>
                  </div>
                  <p>{article.excerpt}</p>
                </div>
              </div>
            ))
          ) : (
            !loading &&
            !error && (
              <div className="col-12">
                <p>No articles available at the moment. Please check back soon.</p>
              </div>
            )
          )}
        </div>
      </div>
    </section>
  );
};

export default ArticlesSection;
